<template>
  <div
    class="suscription-container min-h-screen flex items-center justify-center"
  >
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 z-20 relative">
      <div class="max-w-6xl mx-auto">
        <div class="rounded-2xl p-2 md:p-10">
          <h2
            class="text-3xl lg:text-6xl font-bold text-white text-center mb-2"
          >
            Manténte conectado
          </h2>
          <h2 class="text-xl lg:text-2xl font-bold text-white text-center mb-8">
            Regístrate para recibir más información sobre eventos, actividades y
            promociones de Hike Society
          </h2>
          <div
            class="flex flex-col md:flex-row items-stretch justify-center mb-8"
          >
            <div class="flex-grow max-w-3xl w-full relative">
              <div class="flex">
                <div class="bg-white p-3 rounded-l-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <input
                  type="email"
                  v-model="email"
                  placeholder="Correo electrónico"
                  class="w-full px-4 py-3 border-0 focus:ring-0 focus:outline-none"
                  :class="{ 'border-red-500': validationError }"
                />
              </div>
              <span
                v-if="validationError"
                class="text-white text-sm absolute left-0 -bottom-6"
                >{{ validationError }}</span
              >
            </div>

            <button
              @click="subscribe"
              :disabled="isSubmitting"
              class="bg-[#99b642] hover:bg-[#8ba73b] text-white px-6 py-3 rounded-r-lg flex items-center justify-center mt-2 md:mt-0"
            >
              <span v-if="isSubmitting">Procesando...</span>
              <span v-else>Suscribirse</span>
              <svg
                v-if="!isSubmitting"
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 ml-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>

          <div class="text-center" v-if="subscriptionStatus">
            <p
              :class="
                subscriptionStatus.success ? 'text-green-400' : 'text-white'
              "
              class="p-3 rounded-lg"
            >
              {{ subscriptionStatus.message }}
            </p>
          </div>

          <p class="text-white text-center text-sm">
            Al hacer click en el botón, reconoces que has leído
            <a href="/terminos-y-condiciones" class="underline"
              >el Aviso de Privacidad</a
            >
            y das tu consentimiento para suscribirte al boletín de Columbia
            Sportswear.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import axios from "@/axios";
import Swal from "sweetalert2";

const email = ref("");
const isSubmitting = ref(false);
const validationError = ref("");
const subscriptionStatus = ref(null);

const validateEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

const subscribe = async () => {
  // Reset validation and status
  validationError.value = "";
  subscriptionStatus.value = null;

  // Validate email
  if (!email.value) {
    validationError.value = "Por favor, ingresa tu correo electrónico";
    return;
  }

  if (!validateEmail(email.value)) {
    validationError.value = "Por favor, ingresa un correo electrónico válido";
    return;
  }

  isSubmitting.value = true;

  try {
    // Send to API
    const response = await axios.post("/subscribe-form", {
      email: email.value,
    });

    response.data;
    Swal.fire({
      icon: "success",
      title: "¡Gracias por suscribirte!",
      text: "Recibirás nuestras novedades pronto.",
      timer: 3000,
      toast: true,
      position: "top-right",
      showConfirmButton: false,
    });

    // Handle success
    subscriptionStatus.value = {
      success: true,
      message: "¡Gracias por suscribirte! Recibirás nuestras novedades pronto.",
    };

    // Clear form
    email.value = "";
  } catch (error) {
    // Handle error
    Swal.fire({
      icon: "error",
      title: "Error",
      text:
        error.response?.data?.message ||
        "Hubo un error al procesar tu solicitud. Por favor, intenta nuevamente.",
      toast: true,
      position: "top-right",
      timer: 3000,
      showConfirmButton: false,
    });
    subscriptionStatus.value = {
      success: false,
      message:
        error.response?.data?.message ||
        "Hubo un error al procesar tu solicitud. Por favor, intenta nuevamente.",
    };

    console.error("Error en suscripción:", error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.suscription-container {
  background-image: url("@/assets/img/bg-main.png");
  background-size: cover;
  background-position: center;
}

@media (max-width: 768px) {
  .auth-container::before {
    background-size: 300px;
  }
}

/* Efecto de animación sutil para los elementos */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.backdrop-blur-md {
  animation: fadeIn 0.6s ease-out forwards;
}
</style>
